import {useCallback, useEffect, useState} from 'react'
import {View} from 'react-native'
import {plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {sanitizeHandle} from '#/lib/strings/handles'
import {logger} from '#/logger'
import {
  type Classroom,
  type ClassroomEdits,
  levelChangeLockedUntil,
  STANDARD_LEVELS,
  useClassroomActor,
} from '#/state/classrooms'
import {
  type ClassroomViewer,
  useUpdateClassroomMutation,
} from '#/state/queries/classrooms'
import {useAgent} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {
  ClassroomFields,
  type ClassroomFormValues,
  effectiveLevel,
  effectiveLevelNameType,
  effectiveParentsCanJoin,
} from './ClassroomFields'
import {
  putClassroomFace,
  renameClassroomFace,
  uploadClassroomCover,
} from './publishClassroomFace'
import {showClassroomError} from './showClassroomError'

/**
 * Changing a classroom you run.
 *
 * The profile's own pattern, one screen over: what you are is edited where you
 * are, by the account that is it, through a form that looks like the one that
 * made it. A classroom that cannot be corrected is a classroom you have to
 * delete and rebuild to fix a typo in its name.
 *
 * The address is shown but not editable. A classroom's handle is in every link
 * that has been handed out (`/classroom/<handle>/<id>`), so renaming the room
 * renames what people read without moving where they were sent.
 */
export function EditClassroomDialog({
  control,
  classroom,
  viewer,
}: {
  control: DialogControlProps
  classroom: Classroom
  /** What the server says the reader may do here. */
  viewer: ClassroomViewer
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const actor = useClassroomActor()
  const agent = useAgent()
  const update = useUpdateClassroomMutation(classroom.id)
  const canEdit = viewer.canManage
  /* The cover blob and the public face live in the owner's repo. */
  const isOwner = !!actor && actor.did === classroom.ownerDid
  const [saving, setSaving] = useState(false)

  const parents = classroom.publishedCounts?.parents ?? 0
  /*
   * The parents rule is the room's to change only while no parent holds a
   * seat: a seat already given cannot be taken back by an edit, so the choice
   * is out of reach the moment somebody is in it. The server enforces exactly
   * this; the copy is how the form says so.
   */
  const lockedParentsReason =
    parents > 0
      ? plural(parents, {
          one: 'One parent is in this classroom, so this cannot be changed.',
          other: '# parents are in this classroom, so this cannot be changed.',
        })
      : undefined

  /*
   * The level may move once a month (`levelChangeLockedUntil`), so while it is
   * held the field is held with it, and the form says when it moves again rather
   * than letting an owner fill in an answer the store will refuse.
   *
   * Only the wait and the day it ends: *why* a classroom changes its level slowly
   * is the school's business, and the owner reading this already knows what they
   * are trying to do.
   */
  const levelLockedUntil = levelChangeLockedUntil(classroom)
  let lockedLevelReason: string | undefined
  if (levelLockedUntil) {
    const days = Math.max(
      1,
      Math.ceil(
        (levelLockedUntil.getTime() - Date.now()) / (24 * 60 * 60 * 1000),
      ),
    )
    lockedLevelReason =
      days === 1
        ? l`The level was changed recently. It can be changed again tomorrow.`
        : plural(days, {
            one: 'The level was changed recently. It can be changed again in # day.',
            other:
              'The level was changed recently. It can be changed again in # days.',
          })
  }

  const [values, setValues] = useState<ClassroomFormValues>(() =>
    valuesFrom(classroom),
  )

  /**
   * The classroom is the truth, so the form follows it whenever it changes.
   *
   * Saving a change is what makes it change: without this, closing runs `onClose`
   * with the room this render closed over — the one from *before* the save — and
   * the next open would show the old values, with a save putting them back.
   */
  useEffect(() => {
    setValues(valuesFrom(classroom))
  }, [classroom])

  const onChange = useCallback(
    <K extends keyof ClassroomFormValues>(
      key: K,
      value: ClassroomFormValues[K],
    ) => setValues(prev => ({...prev, [key]: value})),
    [],
  )

  /*
   * A year is asked for when a classroom is *made*; an edit may leave the room
   * as it is. A room that predates years reads as its whole level, and an owner
   * fixing a typo should not have to answer a new question to save it. A SYSTEM
   * level is the one field that cannot be emptied, because the school wrote it
   * itself and there is no shared name behind it to fall back on.
   */
  const canSubmit =
    canEdit &&
    !saving &&
    values.name.trim().length > 0 &&
    (effectiveLevelNameType(values) !== 'SYSTEM' ||
      effectiveLevel(values).trim().length > 0)

  const onClose = useCallback(
    () => setValues(valuesFrom(classroom)),
    [classroom],
  )

  const onSubmit = async () => {
    if (!canSubmit || !actor) return
    setSaving(true)
    const edits = changedEdits(values, valuesFrom(classroom))
    const face = {
      id: classroom.id,
      name: values.name,
      handle: classroom.handle,
      createdAt: classroom.createdAt,
    }
    const coverChanged = isOwner && values.cover !== currentCover(classroom)
    try {
      /*
       * The form starts from the cover the room shows, so an untouched cover is
       * sent as nothing; an emptied one is removed, and new bytes are uploaded
       * and kept referenced by the room's public face before the server is told.
       */
      if (coverChanged) {
        if (values.cover) {
          const {cid, blob} = await uploadClassroomCover(agent, values.cover)
          await putClassroomFace(agent, face, blob)
          edits.coverCid = cid
        } else {
          await putClassroomFace(agent, face)
          edits.coverCid = ''
        }
      }
      await update.mutateAsync(edits)
    } catch (error) {
      setSaving(false)
      showClassroomError(i18n, error)
      return
    }
    /*
     * A rename reaches the public face too, so link previews do not keep the old
     * name. The room is saved either way, so a failure here is only logged.
     */
    if (isOwner && !coverChanged && values.name !== classroom.name) {
      await renameClassroomFace(agent, face).catch(error => {
        logger.error('Failed to rename a classroom public face', {
          safeMessage: error,
        })
      })
    }
    setSaving(false)
    Toast.show(l`Classroom updated`)
    control.close()
  }

  return (
    <Dialog.Outer control={control} onClose={onClose}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={l`Edit ${classroom.name}`}>
        <View style={[a.gap_lg]}>
          <Text style={[a.text_xl, a.font_bold]}>{l`Edit classroom`}</Text>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            {l`Only the account that runs this classroom can change it. Its address stays ${sanitizeHandle(classroom.handle)} so the links you handed out keep working.`}
          </Text>

          {!canEdit ? (
            <Text
              testID="classroomEditBlocked"
              style={[a.text_md, {color: t.palette.negative_500}]}>
              {l`This classroom belongs to another account.`}
            </Text>
          ) : null}

          <ClassroomFields
            values={values}
            onChange={onChange}
            /*
             * The room's own country, and the school's only as a fallback: a
             * room carries the country it was made in, and an edit is not where
             * it changes.
             */
            country={classroom.country ?? actor?.country}
            lockedParentsReason={lockedParentsReason}
            lockedLevelReason={lockedLevelReason}
            coverEditable={isOwner}
          />

          <Button
            testID="classroomEditSubmit"
            label={l`Save changes`}
            size="large"
            color="primary"
            disabled={!canSubmit}
            onPress={() => void onSubmit()}>
            <ButtonText>{l`Save changes`}</ButtonText>
          </Button>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/** The fields the form would write, in the server's shape. */
function editsFrom(values: ClassroomFormValues): ClassroomEdits {
  return {
    name: values.name,
    description: values.description,
    visibility: values.visibility,
    level: effectiveLevel(values),
    grade: values.grade,
    levelNameType: effectiveLevelNameType(values),
    /* `''` clears the school's own level name. */
    levelLabel: values.levelLabel,
    international: values.international,
    parentsCanJoin: effectiveParentsCanJoin(values),
    interests: values.interests,
  }
}

/**
 * Only what the owner changed.
 *
 * An unchanged field is not sent, so the server never reads a field it already
 * holds as an edit - a grade-less room's `grade: ''` would otherwise count as
 * a level change, with an announcement and a month's lock. The level, its year
 * and its kind travel together: the server clears the year when only the level
 * moves.
 */
export function changedEdits(
  values: ClassroomFormValues,
  before: ClassroomFormValues,
): ClassroomEdits {
  const next = editsFrom(values)
  const prev = editsFrom(before)
  const edits: ClassroomEdits = {}
  for (const key of Object.keys(next) as (keyof ClassroomEdits)[]) {
    if (JSON.stringify(next[key]) !== JSON.stringify(prev[key])) {
      Object.assign(edits, {[key]: next[key]})
    }
  }
  if (
    edits.level !== undefined ||
    edits.grade !== undefined ||
    edits.levelNameType !== undefined
  ) {
    edits.level = next.level
    edits.grade = next.grade
    edits.levelNameType = next.levelNameType
  }
  return edits
}

/** The classroom as it is now, as the form's starting values. */
function valuesFrom(classroom: Classroom): ClassroomFormValues {
  return {
    name: classroom.name,
    description: classroom.description ?? '',
    cover: currentCover(classroom),
    parentsCanJoin: classroom.parentsCanJoin ?? true,
    visibility: classroom.visibility,
    levelNameType: classroom.levelNameType,
    // A SYSTEM level ("System 9") is not one of the shared ones, so it belongs
    // in the free-text field rather than being silently swapped for a shared
    // level the school never chose — and *no level* is a choice of its own that
    // has to survive a round trip through this form, not become Primary School.
    level:
      classroom.level === '' ||
      (STANDARD_LEVELS as readonly string[]).includes(classroom.level)
        ? classroom.level
        : STANDARD_LEVELS[1],
    grade: classroom.grade ?? '',
    systemLevel: classroom.levelNameType === 'SYSTEM' ? classroom.level : '',
    levelLabel: classroom.levelLabel ?? '',
    international: classroom.international ?? false,
    interests: classroom.interests,
  }
}

/** The cover the room shows now: the server's, or the one kept on this device. */
function currentCover(classroom: Classroom): string {
  return classroom.coverUrl ?? classroom.cover ?? ''
}
